/*
*+------------------------------------------------------------------------+
*| Licensed Materials - Property of IBM
*| IBM Cognos Products: BI Dashboard
*| (C) Copyright IBM Corp. 2017
*|
*| US Government Users Restricted Rights - Use, duplication or disclosure
*| restricted by GSA ADP Schedule Contract with IBM Corp.
*+------------------------------------------------------------------------+
*/
define([
	'../../../../lib/@waca/core-client/js/core-client/ui/core/Class'
],
function(Class) {
	'use strict';

	/**
	 * [todo] livewidget-cleanup: we dont need to fix this file, since it is not used anymore.
	 */
	/**
	* INTENT: LocalAggregateSortInfo is used to construct a local aggregrate sort info object.
	*		This sort info is used when performing local client side sort processing.
	*/

	var LocalAggregateSortInfo = Class.extend({

		/**
		* @Constructor
		* @param {Object} visAPI
		*/
		init: function(visAPI) {
			LocalAggregateSortInfo.inherited('init', this, arguments);

			this.visAPI = visAPI;
		},

		/**
		* @return {Object} Returns an object that contains local aggregate sort details
		* or null if it doesn't support local client sort.
		*/
		getSortInfo: function() {
			if (!this._doesVisualizationSupportOperation()) {
				return null;
			}

			var sortInfo = this._getAggregateSortInfo();
			if (!sortInfo) {
				return null;
			}

			return sortInfo;
		},

		/**
		* @return {Boolean} Returns true if the visualization supports local aggregate sort
		*/
		_doesVisualizationSupportOperation: function() {
			return this.visAPI.getDefinition().aggregateAndSortOnClient === true;
		},

		/**
		* @param {Object} sort a sortType object
		* @return {Boolean} Returns true if the sortType is compatible with the local aggregate sort operation
		*/
		_isSortCompatibleWithOperation: function(sort) {
			return !!(sort && sort.isSecondary !== true);
		},

		/**
		* @param {Object} mappedSlot The mapped slot to obtain sort information from
		* @return {Object} Returns an object that contains local aggregate sort details
		*/
		_getSlotSortInfo: function(mappedSlot) {
			const slotDefinition = mappedSlot.getDefinition();
			const operationDefinition = slotDefinition.clientSideAggregatedAndSortInfo;
			if (!operationDefinition) {
				return;	//This slot does not support local sort
			}

			const sort = mappedSlot.getSortType();
			if (!this._isSortCompatibleWithOperation(sort)) {
				return;	//Cannot apply sort
			}

			if (!this._areRequiredSlotsForSortingMapped(operationDefinition)) {
				return;	//Conditions for local sort not satisfied
			}

			const groupSlot = this.visAPI.getDataSlotById(operationDefinition.groupOnColumnId);
			if (!groupSlot || !groupSlot.isMapped()) {
				return;	//Group slot has no mapping - sort does not apply
			}

			const existingGroupSort = groupSlot.getSortType();
			if (this._isSortCompatibleWithOperation(existingGroupSort)) {
				return;	//Grouped slot's existing sort vetos local sort
			}

			const groupColumns = groupSlot.getDataItemAPIs().map(function(dataItem) { return dataItem.getUniqueId(); });
			const aggregateColumn = mappedSlot.getDataItemAPI(0).getUniqueId();

			// Secondary sort in each bin of the group. Only able to handle one slot in mappedSlotCondition of the vis definition.
			const mappedConditionSlot = this.visAPI.getDataSlotById(operationDefinition.mappedSlotCondition[0]);

			return {
				order: sort,
				groupColumns: groupColumns,
				aggregateColumn: aggregateColumn,
				// DataItems dropped in same slot are grouped to a new nested dataItem and the new data item is mapped to this slot. 
				// So the following works for single and nested data items.
				mappedSlotConditionDataItemUid: mappedConditionSlot.getDataItemAPI(0).getUniqueId()
			};
		},

		/**
		* @return {Object} Returns an object that contains local aggregate sort details from the first
		* mapped slot that supports local client sort operation.
		* If the visualization defines an extra step to aggregate and sort the result before rendering it.  This is
		* needed to allow sorting on the ordinal slot when there's a color slot mapped
		*/
		_getAggregateSortInfo: function() {
			var mappedSlots = this.visAPI.getMappedDataSlots();
			for (var i = 0; i < mappedSlots.length; i++) {
				var sortInfo = this._getSlotSortInfo(mappedSlots[i]);
				if (sortInfo) {
					return sortInfo;
				}
			}
			return null;
		},

		/**
		* @param {Object} operationDefinition The clientSideAggregatedAndSortInfo from a slot definition.
		* @return {Boolean} Returns true if there are required slots for sorting are mapped.
		* We should only need to do aggregation and sorting on the client if the color slot is actually mapped.  This method
		* will check if the 'mappedSlotCondition' (usually set to 'color') is fufilled.
		*/
		_areRequiredSlotsForSortingMapped: function(operationDefinition) {
			var slotConditions = operationDefinition.mappedSlotCondition;
			if (!slotConditions) {
				return true;	//No conditions to verify, so slot is automatically applicable
			}

			for (var i = 0; i < slotConditions.length; i++) {
				var requiredSlotId = slotConditions[i];
				var requiredSlot = this.visAPI.getDataSlotById(requiredSlotId);
				if (!requiredSlot || !requiredSlot.isMapped()) {
					return false;
				}
			}

			return true;
		}
	});

	return LocalAggregateSortInfo;
});
